import { Button } from '@highmobility/console-ui-components';
import { FC, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { ApplicationSinkSettings, AzureBlobStorageSinkSettings, ProjectInstance } from '@/types';

import { AzureDataBatchingThreshold } from './AzureDataBatchingThreshold';

export type AzureDataBatchingThresholdsProps = {
  teamId: string;
  projectId: string;
  instance: ProjectInstance;
  onChange: (newSettings: ApplicationSinkSettings['sink']) => void;
  sinkSettings: AzureBlobStorageSinkSettings;
  saveDisabled: boolean;
};

export const AzureDataBatchingThresholds: FC<AzureDataBatchingThresholdsProps> = observer(
  (props) => {
    const [updatedSettings, setUpdatedSettings] = useState<AzureBlobStorageSinkSettings>(
      props.sinkSettings
    );

    return (
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          <h5 className="text-heading-5">Data Batching Thresholds</h5>
          <p className="text-body-small text-white-60">
            Here you can configure the data batching threshold among three parameters. We accumulate
            data updates on our side until one of the following conditions are met. When a condition
            is met all accumulated data is pushed to your Azure Blob Storage and the batching
            process starts again. Note that the thresholds are approximate and may vary between
            different push cycles.
          </p>
        </div>
        <div className="flex flex-col gap-12">
          <AzureDataBatchingThreshold
            title="Message Count"
            subtitle="(max 10.000)"
            name="reachMessageCount"
            initialValue={props.sinkSettings.options.count}
            range={{ min: 1000, max: 10000 }}
            getThumbText={(value) => `${value} messages`}
            step={10}
            onChange={(e) => {
              setUpdatedSettings({
                type: 'azure_blob_storage',
                options: {
                  ...props.sinkSettings.options,
                  count: Number(e.currentTarget.value),
                },
              });
            }}
          />
          <AzureDataBatchingThreshold
            title="Message Size"
            subtitle="(max 200MB)"
            name="reachMessageSize"
            initialValue={Math.floor(props.sinkSettings.options.byte_size / 1024 / 1024)} // bytes => MB
            range={{ min: 10, max: 200 }}
            getThumbText={(value) => `${value}MB`}
            step={10}
            onChange={(e) =>
              setUpdatedSettings({
                type: 'azure_blob_storage',
                options: {
                  ...props.sinkSettings.options,
                  byte_size: Number(e.currentTarget.value) * 1024 * 1024, // MB => bytes
                },
              })
            }
          />
          <AzureDataBatchingThreshold
            title="Time"
            subtitle="(max 24h)"
            name="reachStorageTime"
            initialValue={Math.floor(props.sinkSettings.options.timer_in_seconds / 60)} // seconds => min
            range={{ min: 10, max: 1440 }}
            getThumbText={(value) => {
              const hours = Math.floor(value / 60);
              const minutes = value % 60;
              if (hours === 0) return `${value}min`;
              if (minutes === 0) return `${hours}h`;
              return `${hours}h ${minutes}min`;
            }}
            step={10}
            onChange={(e) =>
              setUpdatedSettings({
                type: 'azure_blob_storage',
                options: {
                  ...props.sinkSettings.options,
                  timer_in_seconds: Number(e.currentTarget.value) * 60, // min => seconds
                },
              })
            }
          />
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button
            aria-label="Save and update thresholds"
            className="ml-auto"
            disabled={props.saveDisabled}
            intent="primary"
            onClick={() => {
              props.onChange(updatedSettings);
            }}
          >
            Save and update
          </Button>
        </div>
      </div>
    );
  }
);
